import {
  Badge,
  Box,
  Divider,
  Flex,
  Grid,
  Icon,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import Button from 'components/ui/Button';
import { ESTADO_META, formatFecha } from 'constants/episodeMetadata';
import {
  AlertCircle,
  CheckCircle2,
  ClipboardList,
  Edit3,
  FlaskConical,
  FolderOpen,
  Microscope,
  PlayCircle,
  Stethoscope,
} from 'lucide-react';
import React from 'react';
import { ClinicalEpisode, EstadoEpisodio, PhysicalEvaluation } from 'types/models';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface EpisodeCardProps {
  episode: ClinicalEpisode;
  onDiagnoseClick?: () => void;
  onEditClick?: () => void;
  onStartTreatmentClick?: () => void;
  onCloseClick?: () => void;
  onArchiveClick?: () => void;
  onReopenClick?: () => void;
  isTransitioning?: boolean;
  evaluations?: PhysicalEvaluation[];
  onNewEvaluation?: () => void;
  onViewEvaluation?: (ev: PhysicalEvaluation) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const EVA_EMOJIS = ['😊','😊','🙂','🙂','😐','😐','😕','😕','😢','😢','😭'];

function evaColor(n: number): string {
  if (n <= 3) return 'green';
  if (n <= 6) return 'orange';
  return 'red';
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  const color = useColorModeValue('secondaryGray.500', 'secondaryGray.400');
  return (
    <Text
      fontSize='10px'
      fontWeight='800'
      color={color}
      textTransform='uppercase'
      letterSpacing='0.1em'
      mb='6px'>
      {children}
    </Text>
  );
}

function FieldBlock({
  label,
  value,
  mono,
  clamp = 3,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
  clamp?: number;
}) {
  const labelColor = useColorModeValue('secondaryGray.500', 'secondaryGray.400');
  const textColor  = useColorModeValue('secondaryGray.900', 'white');
  if (!value) return null;
  return (
    <Box>
      <Text fontSize='10px' fontWeight='700' color={labelColor} textTransform='uppercase' letterSpacing='0.08em' mb='2px'>
        {label}
      </Text>
      <Text
        fontSize='sm'
        fontWeight='500'
        color={textColor}
        fontFamily={mono ? 'mono' : undefined}
        noOfLines={clamp}
        lineHeight='1.55'>
        {value}
      </Text>
    </Box>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

export default function EpisodeCard({
  episode,
  onDiagnoseClick,
  onEditClick,
  onStartTreatmentClick,
  onCloseClick,
  onArchiveClick,
  onReopenClick,
  isTransitioning = false,
  evaluations,
  onNewEvaluation,
  onViewEvaluation,
}: EpisodeCardProps) {
  const cfg = ESTADO_META[episode.estado];

  const isActive = episode.estado === EstadoEpisodio.ABIERTO || episode.estado === EstadoEpisodio.EN_TRATAMIENTO;
  const hasDx    = !!(episode.diagnosticoPrincipal || episode.codigoCie10);
  const hasActions =
    onDiagnoseClick || onEditClick || onStartTreatmentClick ||
    onCloseClick || onArchiveClick || onReopenClick;

  // Colors
  const cardBg        = useColorModeValue('white', 'navy.800');
  const sectionBg     = useColorModeValue('gray.50', 'navy.750');
  const borderColor   = useColorModeValue('gray.100', 'whiteAlpha.100');
  const textColor     = useColorModeValue('secondaryGray.900', 'white');
  const mutedColor    = useColorModeValue('secondaryGray.500', 'secondaryGray.400');
  const noteColor     = useColorModeValue('secondaryGray.700', 'secondaryGray.300');
  const dxBg          = useColorModeValue(`${cfg.colorScheme}.50`, `${cfg.colorScheme}.900`);
  const dxBorder      = useColorModeValue(`${cfg.colorScheme}.200`, `${cfg.colorScheme}.700`);
  const dxText        = useColorModeValue(`${cfg.colorScheme}.800`, `${cfg.colorScheme}.100`);
  const dxMuted       = useColorModeValue(`${cfg.colorScheme}.500`, `${cfg.colorScheme}.300`);

  return (
    <Box bg={cardBg} overflow='hidden'>

      {/* ── Motivo de consulta ──────────────────────────────────────────── */}
      <Box px='16px' pt='14px' pb='12px'>
        <SectionLabel>Motivo de consulta</SectionLabel>
        <Text
          fontSize='sm'
          fontWeight='600'
          color={textColor}
          lineHeight='1.6'>
          {episode.motivoConsulta}
        </Text>
      </Box>

      <Divider borderColor={borderColor} />

      {/* ── Diagnóstico ─────────────────────────────────────────────────── */}
      <Box px='16px' py='12px'>
        {hasDx ? (
          <Box
            bg={dxBg}
            border='1px solid'
            borderColor={dxBorder}
            borderLeft='4px solid'
            borderLeftColor={cfg.dot}
            borderRadius='10px'
            px='12px'
            py='10px'>
            <Flex align='flex-start' gap='10px'>
              <Stethoscope size={16} color={cfg.dot} style={{ flexShrink: 0, marginTop: 2 }} />
              <Box flex={1} minW={0}>
                {episode.codigoCie10 && (
                  <Badge
                    fontFamily='mono'
                    fontSize='11px'
                    fontWeight='800'
                    colorScheme={cfg.colorScheme}
                    borderRadius='6px'
                    px='7px'
                    mb='4px'>
                    {episode.codigoCie10}
                  </Badge>
                )}
                {episode.diagnosticoPrincipal && (
                  <Text fontSize='sm' fontWeight='600' color={dxText} lineHeight='1.5' mb='2px'>
                    {episode.diagnosticoPrincipal}
                  </Text>
                )}
                {episode.diagnosticoSecundario && (
                  <Text fontSize='xs' color={dxMuted} lineHeight='1.4'>
                    + {episode.diagnosticoSecundario}
                  </Text>
                )}
              </Box>
            </Flex>
          </Box>
        ) : isActive ? (
          /* No diagnosis CTA */
          <Flex
            align='center'
            gap='10px'
            px='12px'
            py='10px'
            borderRadius='10px'
            border='1.5px dashed'
            borderColor={onDiagnoseClick ? `${cfg.colorScheme}.200` : 'gray.200'}
            cursor={onDiagnoseClick ? 'pointer' : 'default'}
            onClick={onDiagnoseClick}
            role={onDiagnoseClick ? 'button' : undefined}
            _hover={onDiagnoseClick ? { bg: dxBg, borderColor: cfg.dot } : undefined}
            transition='all 0.15s'>
            <AlertCircle size={18} color={onDiagnoseClick ? cfg.dot : '#CBD5E0'} style={{ flexShrink: 0 }} />
            <Box>
              <Text fontSize='sm' fontWeight='600' color={onDiagnoseClick ? dxText : mutedColor}>
                Sin diagnóstico registrado
              </Text>
              {onDiagnoseClick && (
                <Text fontSize='xs' color={dxMuted}>
                  Haz clic para añadir diagnóstico
                </Text>
              )}
            </Box>
          </Flex>
        ) : (
          <Text fontSize='xs' color={mutedColor} fontStyle='italic'>Sin diagnóstico</Text>
        )}
      </Box>

      {/* ── Anamnesis ────────────────────────────────────────────────────── */}
      {episode.anamnesis && (() => {
        const a = episode.anamnesis!;
        if (!a.evolucion && !a.sintomas) return null;

        const evaMatch = a.sintomas?.match(/EVA\s*(\d+)/i);
        const evaNum   = evaMatch ? Math.min(parseInt(evaMatch[1]), 10) : null;

        return (
          <>
            <Divider borderColor={borderColor} />
            <Box px='16px' py='12px' bg={sectionBg}>
              <Flex align='center' gap='6px' mb='10px'>
                <ClipboardList size={13} color={mutedColor} />
                <SectionLabel>Anamnesis</SectionLabel>
              </Flex>
              <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap='12px'>
                <FieldBlock label='Evolución' value={a.evolucion} />
                <Box>
                  <Text fontSize='10px' fontWeight='700' color={mutedColor} textTransform='uppercase' letterSpacing='0.08em' mb='2px'>
                    Síntomas
                  </Text>
                  {evaNum !== null && (
                    <Flex align='center' gap='6px' mb='4px'>
                      <Text fontSize='lg' lineHeight='1'>{EVA_EMOJIS[evaNum]}</Text>
                      <Badge colorScheme={evaColor(evaNum)} borderRadius='full' px='7px' fontSize='11px' fontWeight='700'>
                        EVA {evaNum}
                      </Badge>
                    </Flex>
                  )}
                  {a.sintomas && (
                    <Text fontSize='sm' fontWeight='500' color={noteColor} lineHeight='1.55' noOfLines={3}>
                      {a.sintomas}
                    </Text>
                  )}
                </Box>
              </Grid>
            </Box>
          </>
        );
      })()}

      {/* ── Notas apertura / cierre ──────────────────────────────────────── */}
      {(episode.notaApertura || episode.notaCierre) && (
        <>
          <Divider borderColor={borderColor} />
          <Box px='16px' py='12px'>
            <Grid templateColumns={episode.notaApertura && episode.notaCierre ? '1fr 1fr' : '1fr'} gap='12px'>
              {episode.notaApertura && (
                <Box>
                  <Flex align='center' gap='5px' mb='4px'>
                    <FolderOpen size={12} color={mutedColor as string} />
                    <Text fontSize='10px' fontWeight='700' color={mutedColor} textTransform='uppercase' letterSpacing='0.08em'>
                      Nota apertura
                    </Text>
                  </Flex>
                  <Text fontSize='xs' color={noteColor} lineHeight='1.55' noOfLines={3}>
                    {episode.notaApertura}
                  </Text>
                </Box>
              )}
              {episode.notaCierre && (
                <Box>
                  <Flex align='center' gap='5px' mb='4px'>
                    <CheckCircle2 size={12} color='#68D391' />
                    <Text fontSize='10px' fontWeight='700' color={mutedColor} textTransform='uppercase' letterSpacing='0.08em'>
                      Nota cierre
                    </Text>
                  </Flex>
                  <Text fontSize='xs' color={noteColor} lineHeight='1.55' noOfLines={3}>
                    {episode.notaCierre}
                  </Text>
                </Box>
              )}
            </Grid>
          </Box>
        </>
      )}

      {/* ── Evaluaciones físicas ─────────────────────────────────────────── */}
      {evaluations !== undefined && (
        <>
          <Divider borderColor={borderColor} />
          <Box px='16px' py='12px' bg={sectionBg}>
            <Flex align='center' justify='space-between' mb='8px'>
              <Flex align='center' gap='6px'>
                <Microscope size={13} color={mutedColor as string} />
                <Text fontSize='10px' fontWeight='800' color={mutedColor} textTransform='uppercase' letterSpacing='0.1em'>
                  Evaluaciones
                </Text>
                {evaluations.length > 0 && (
                  <Badge colorScheme='teal' borderRadius='full' px='6px' fontSize='9px' fontWeight='700'>
                    {evaluations.length}
                  </Badge>
                )}
              </Flex>
              {onNewEvaluation && (
                <Button
                  size='xs'
                  colorScheme='teal'
                  variant='ghost'
                  leftIcon={<FlaskConical size={11} />}
                  onClick={onNewEvaluation}>
                  Nueva
                </Button>
              )}
            </Flex>

            {evaluations.length === 0 ? (
              <Text fontSize='xs' color={mutedColor} fontStyle='italic'>
                Sin evaluaciones registradas
              </Text>
            ) : (
              <Flex direction='column' gap='4px'>
                {evaluations.slice(0, 3).map((ev) => {
                  const romCount = ev.rangoMovimiento ? Object.keys(ev.rangoMovimiento).length : 0;
                  const posCount = ev.pruebasEspecificas
                    ? Object.values(ev.pruebasEspecificas).filter(p => p.resultado === 'positivo').length
                    : 0;
                  return (
                    <Flex
                      key={ev.id}
                      align='center'
                      gap='8px'
                      px='10px'
                      py='6px'
                      borderRadius='8px'
                      bg={cardBg}
                      border='1px solid'
                      borderColor={borderColor}
                      cursor={onViewEvaluation ? 'pointer' : 'default'}
                      _hover={onViewEvaluation ? { borderColor: 'teal.200', bg: 'teal.50' } : undefined}
                      transition='all 0.12s'
                      onClick={onViewEvaluation ? () => onViewEvaluation(ev) : undefined}>
                      <Flex
                        w='22px'
                        h='22px'
                        borderRadius='6px'
                        bg='teal.50'
                        align='center'
                        justify='center'
                        flexShrink={0}>
                        <Text fontSize='9px' fontWeight='800' color='teal.500'>#{ev.numeroEvaluacion}</Text>
                      </Flex>
                      <Text fontSize='xs' fontWeight='600' color={noteColor} flex={1} noOfLines={1}>
                        {ev.diagnostico ?? formatFecha(ev.fechaEvaluacion)}
                      </Text>
                      <Flex gap='4px' flexShrink={0}>
                        {ev.escalaDolor != null && (
                          <Badge colorScheme={evaColor(ev.escalaDolor)} borderRadius='full' px='5px' fontSize='9px'>
                            EVA {ev.escalaDolor}
                          </Badge>
                        )}
                        {romCount > 0 && (
                          <Badge colorScheme='blue' variant='subtle' borderRadius='full' px='5px' fontSize='9px'>
                            {romCount} ROM
                          </Badge>
                        )}
                        {posCount > 0 && (
                          <Badge colorScheme='orange' variant='subtle' borderRadius='full' px='5px' fontSize='9px'>
                            {posCount}+
                          </Badge>
                        )}
                      </Flex>
                    </Flex>
                  );
                })}
                {evaluations.length > 3 && (
                  <Text fontSize='10px' color={mutedColor} textAlign='center' pt='2px'>
                    +{evaluations.length - 3} más
                  </Text>
                )}
              </Flex>
            )}
          </Box>
        </>
      )}

      {/* ── Action bar ───────────────────────────────────────────────────── */}
      {hasActions && (
        <>
          <Divider borderColor={borderColor} />
          <Flex px='16px' py='10px' gap='8px' flexWrap='wrap' bg={cardBg}>
            {onDiagnoseClick && (
              <Button
                size='sm'
                colorScheme='teal'
                variant='outline'
                leftIcon={<Stethoscope size={14} />}
                onClick={onDiagnoseClick}>
                Diagnóstico
              </Button>
            )}
            {onEditClick && (
              <Button
                size='sm'
                variant='outline'
                leftIcon={<Edit3 size={14} />}
                onClick={onEditClick}>
                Actualizar
              </Button>
            )}
            {onStartTreatmentClick && (
              <Button
                size='sm'
                colorScheme='orange'
                variant='outline'
                leftIcon={<PlayCircle size={14} />}
                isLoading={isTransitioning}
                onClick={onStartTreatmentClick}>
                Iniciar Tratamiento
              </Button>
            )}
            {onCloseClick && (
              <Button
                size='sm'
                colorScheme='green'
                leftIcon={<CheckCircle2 size={14} />}
                onClick={onCloseClick}>
                Dar de Alta
              </Button>
            )}
            {onArchiveClick && (
              <Button
                size='sm'
                colorScheme='gray'
                variant='outline'
                isLoading={isTransitioning}
                onClick={onArchiveClick}>
                Archivar
              </Button>
            )}
            {onReopenClick && (
              <Button
                size='sm'
                colorScheme='blue'
                variant='outline'
                isLoading={isTransitioning}
                onClick={onReopenClick}>
                Reabrir
              </Button>
            )}
          </Flex>
        </>
      )}
    </Box>
  );
}
